import { Fragment } from "react";
import { DashboardViewBoxProps } from "../Dashboard";
import { ChartData } from 'chart.js';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { StyleSheet, Text, View } from "react-native";
import { FONT } from "../../../value/staticValues";
import { PAGE_URL } from "../../../value/Url";

import { ReactComponent as MoreImg } from '../../../img/ico-prefix-more-dashboard.svg';

import { ReactComponent as CpuBigImg } from '../../../img/ico-vpc-list-cpu-purple-big.svg';
import { ReactComponent as MemBigImg } from '../../../img/ico-vpc-list-memory-purple-big.svg';
import { ReactComponent as NetworkImg } from '../../../img/ico-vpc-list-network.svg';
import { ReactComponent as VolumeImg } from '../../../img/ico-vpc-list-v-disk.svg';
import { ReactComponent as GPUImg } from '../../../img/ico-vpc-list-v-gpu.svg';

import { ReactComponent as StatusStableImg} from "../../../img/ico-status-stable.svg";
import { ReactComponent as StatusBlockImg } from "../../../img/ico-status-block.svg";

import DashboardBox from "../../base/tamplate/DashboardBox";

import ProgressBar from "../component/ProgressBar";
import BoldDoughnutChart from "../component/BoldDoughnutChart";
import DoughnutChart from "../component/DoughnutChart";
import PieChart from "../component/PieChart";
import VerticalProgress from "../component/VerticalProgress";
import SmallProgressBarBox from "../component/SmallProgessBarBox";
import HorizontalProgressBox from "../component/HorizontalProgressBox";
import DoughnutChartBlueBox from "../component/DoughnutChartBlueBox";
import BarChartBox from "../component/BarChartBox";
import MatrixChartBox from "../component/MatrixChartBox";
import { DashboardInfra } from "../DashboardContainer";
import { convertUnit, getUnit, percentValue } from "../../../common/util";


interface DashboardInfraProps {
    data: DashboardInfra;
}

export default function InfraViewBox(props:DashboardInfraProps) {
    const {t} = useTranslation();
    const navigate = useNavigate();

    let hostClusterChartData:ChartData<'pie'> = {
        labels: ["Normal", "Compute", "Gpu", "Memory"],
        datasets: [
            {
                data: [props.data.hostCluster.normalCount, props.data.hostCluster.computeCount, props.data.hostCluster.gpuCount, props.data.hostCluster.memoryCount],
                backgroundColor: ["#cdd3db", "#87dbbb", "#a99ce1", "#e4a9b2"],
                hoverBackgroundColor: [ "#9ca3ad", "#5b9782", "#806dc6", "#ae4f5a" ],
                borderWidth: 0,
            },
        ],
    }
    
    const hostCpuChartData:ChartData<'doughnut'> = {
        labels: ["cpu"],
        datasets: [
            {
                data: [percentValue(props.data.host.cpuUsedCount, props.data.host.cpuTotalCount), 100-percentValue(props.data.host.cpuUsedCount, props.data.host.cpuTotalCount)],
                backgroundColor: [
                    "#ce4ffd",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }

    const hostMemoryChartData:ChartData<'doughnut'> = {
        labels: ["memory"],
        datasets: [
            {
                data: [percentValue(props.data.host.memoryUsedCapacityMb, props.data.host.memoryTotalCapacityMb), 100-percentValue(props.data.host.memoryUsedCapacityMb, props.data.host.memoryTotalCapacityMb)],
                backgroundColor: [
                    "#7b4fff",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }

    const hostGpuChartData:ChartData<'doughnut'> = {
        labels: ["gpu"],
        datasets: [
            {
                data: [percentValue(props.data.host.gpuUsedCount, props.data.host.gpuTotalCount), 100-percentValue(props.data.host.gpuUsedCount, props.data.host.gpuTotalCount)],
                backgroundColor: [
                    "#806dc6",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }


    const storageChartData:ChartData<'doughnut'> = {
        labels: ["storage"],
        datasets: [
            {
                data: [percentValue(props.data.storage.usedCapacityGb, props.data.storage.totalCapacityGb), 100-percentValue(props.data.storage.usedCapacityGb, props.data.storage.totalCapacityGb)],
                backgroundColor: [
                    "#06c69d",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }


    const nodeCpuChartData:ChartData<'doughnut'> = {
        labels: ["nodeCpu"],
        datasets: [
            {
                data: [percentValue(props.data.node.cpuUsedCount, props.data.node.cpuTotalCount), 100-percentValue(props.data.node.cpuUsedCount, props.data.node.cpuTotalCount)],
                backgroundColor: [
                    "#ce4ffd",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }

    const nodeMemoryChartData:ChartData<'doughnut'> = {
        labels: ["nodeMemory"],
        datasets: [
            {
                data: [percentValue(props.data.node.memoryUsedCapacityMb, props.data.node.memoryTotalCapacityMb), 100-percentValue(props.data.node.memoryUsedCapacityMb, props.data.node.memoryTotalCapacityMb)],
                backgroundColor: [
                    "#7b4fff",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }



    const nodeStorageChartData:ChartData<'doughnut'> = {
        labels: ["nodeStorage"],
        datasets: [
            {
                data: [percentValue(props.data.node.storageUsedCapacityGb, props.data.node.storageTotalCapacityGb), 100-percentValue(props.data.node.storageUsedCapacityGb, props.data.node.storageTotalCapacityGb)],
                backgroundColor: [
                    "#06c69d",
                    "#f5f5f5",
                ],
                borderWidth: 0,
                borderRadius: 40,
                borderJoinStyle: "round",
            },
        ],
    }


    return (
        <Fragment>
            {/* 1 Line */}
            <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("REGION_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_RESOURCE_GROUP_REGION}`)}}
                        />
                    </View>
                    <View style={{marginTop: 25}}>
                        <View style={{flexDirection: "row", justifyContent: "center"}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                    {t("TOTAL_TEXT")}
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                {props.data.region.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("AZ_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_RESOURCE_GROUP_AZ}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    up
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.az.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    down
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.az.totalCount - props.data.az.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.az.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("HOST_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    up
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.host.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    down
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.host.totalCount - props.data.host.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.host.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("STORAGE_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_STORAGE}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    up
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.storage.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    down
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.storage.totalCount - props.data.storage.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.storage.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("STORAGE_OSD_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.CONTAINER_LOGICAL_RESOURCE_NODE}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    up
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.storage.osdActiveCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    down
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.storage.osdTotalCount - props.data.storage.osdActiveCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.storage.osdTotalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("NETWORK_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_NETWORK}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    Act
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.network.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    Inact
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.network.totalCount - props.data.network.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.network.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("NETWORK_TYPE_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_NETWORK}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    Int
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.network.totalCount - props.data.network.externalCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    Ext
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.network.externalCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.network.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("PROJECT_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.APPLICATION_PROJECT}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    Act
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.project.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    Inact
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.project.totalCount - props.data.project.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.project.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("ADMIN_ACCOUNT_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.APPLICATION_DOMAIN}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    Act
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.admin.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    Inact
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.admin.totalCount - props.data.admin.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.admin.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("USER_ACCOUNT_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.APPLICATION_ACCOUNT}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    Act
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.user.activeCount}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    Inact
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.user.totalCount - props.data.user.activeCount}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.user.totalCount}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>
            </View>
            <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <PieChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_RESOURCE_GROUP_HOST_CLUSTER}
                    title={t("HOST_CLUSTER_TYPE_TEXT")}
                    data={0}
                    total={props.data.hostCluster.totalCount}
                    chartData={hostClusterChartData}
                    color={"#9ca3ad"}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}
                    title={t("HOST_CPU_USAGE_TEXT")}
                    data={props.data.host.cpuUsedCount}
                    total={props.data.host.cpuTotalCount}
                    percent={percentValue(props.data.host.cpuUsedCount, props.data.host.cpuTotalCount)}
                    chartData={hostCpuChartData}
                    img={<CpuBigImg/>}
                    color={"#ce4ffd"}
                    unit={"vCPU"}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}
                    title={t("HOST_MEMORY_USAGE_TEXT")}
                    data={convertUnit(props.data.host.memoryUsedCapacityMb)}
                    total={convertUnit(props.data.host.memoryTotalCapacityMb)}
                    percent={percentValue(props.data.host.memoryUsedCapacityMb, props.data.host.memoryTotalCapacityMb)}
                    chartData={hostMemoryChartData}
                    img={<MemBigImg/>}
                    color={"#7b4fff"}
                    unit={getUnit(props.data.host.memoryTotalCapacityMb)}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}
                    title={t("HOST_GPU_USAGE_TEXT")}
                    data={props.data.host.gpuUsedCount}
                    total={props.data.host.gpuTotalCount}
                    percent={percentValue(props.data.host.gpuUsedCount, props.data.host.gpuTotalCount)}
                    chartData={hostGpuChartData}
                    img={<GPUImg/>}
                    color={"#806dc6"}
                    unit={"GPU"}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}
                    title={t("STORAGE_USAGE_TEXT")}
                    data={props.data.storage.usedCapacityGb}
                    total={props.data.storage.totalCapacityGb}
                    percent={percentValue(props.data.storage.usedCapacityGb, props.data.storage.totalCapacityGb)}
                    chartData={storageChartData}
                    img={<VolumeImg/>}
                    color={"#06c69d"}
                    unit={"GB"}
                />
            </View>
            <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <DoughnutChartBlueBox
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM}
                    title={t("VM_TEXT")}
                    data={props.data.vm.activeCount}
                    total={props.data.vm.totalCount}
                    action={"Activate"}
                />
                <MatrixChartBox
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VOLUMNE}
                    title={t("VOLUME_TYPE_TEXT")}
                    data={props.data.volume.attachedCount}
                    total={props.data.volume.totalCount}
                />
                <VerticalProgress
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VOLUMNE}
                    title={t("VOLUME_ATTACHED_TEXT")}
                    data={props.data.volume.attachedCount}
                    total={props.data.volume.totalCount}
                    percent={percentValue(props.data.volume.attachedCount, props.data.volume.totalCount)}
                    color={"#0853fb"}
                    unit={"개"}
                />
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("VM_PERFORMANCE_TYPE_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_PERFORMANCE}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.vmType.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>

                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("VM_IMAGE_TITLE_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_IMAGE}`)}}
                            />
                        </View>
                        <View style={{flexDirection: "row", marginTop: 12}}>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusStableImg/>
                                    <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                        Up
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                    {props.data.vmImage.activeCount}
                                </Text>
                            </View>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusBlockImg/>
                                    <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                        Down
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                    {props.data.vmImage.totalCount - props.data.vmImage.activeCount}
                                </Text>
                            </View>
                        </View>
                        <View style={{alignItems: "center", marginTop: "auto"}}>
                            <View style={{...styles.TotalBox, width: "100%"}}>
                                <Text style={styles.TotalBoxText}>
                                    Total {props.data.vmImage.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("SECURITY_GROUP_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_SECURITY}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.securityGroup.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("SUBNET_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_NETWORK}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.subnet.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                <View>
                <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("FLOATING_IP_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_IP}`)}}
                            />
                        </View>
                        <View style={{flexDirection: "row", marginTop: 12}}>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusStableImg/>
                                    <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                        Up
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                    {props.data.floatingIp.activeCount}
                                </Text>
                            </View>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusBlockImg/>
                                    <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                        Down
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                    {props.data.floatingIp.totalCount - props.data.floatingIp.activeCount}
                                </Text>
                            </View>
                        </View>
                        <View style={{alignItems: "center", marginTop: "auto"}}>
                            <View style={{...styles.TotalBox, width: "100%"}}>
                                <Text style={styles.TotalBoxText}>
                                    Total {props.data.floatingIp.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("KEY_PAIR_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_KEY_PAIR}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.keyPair.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                {/* <VerticalProgress
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_IP}
                    title={t("FLOATING_IP_ATTACHED_TEXT")}
                    data={props.data.floatingIp.attachedCount}
                    total={props.data.floatingIp.totalCount}
                    percent={percentValue(props.data.floatingIp.attachedCount, props.data.floatingIp.totalCount)}
                    color={"#0853fb"}
                    unit={"개"}
                /> */}
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("FLOATING_IP_ATTACHED_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_IP}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center", alignItems:"center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("CONNECT_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.floatingIp.attachedCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("INGRESS_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_INGRESS}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.ingress.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("VOLUME_ASSIGNED_SIZE_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_INSTANCE_MGMT_VOLUMNE}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center", alignItems:"center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.volume.volumeTotalSizeGb}
                                </Text>
                                <Text style={{...styles.Text, color: "#333", marginLeft: 5}}>
                                    {"GB"}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("BACKUP_IMAGE_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM_BACKUP_IMAGE}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.volumeBackup.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
            </View>
            <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("CONTAINER_CLUSTER_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_LOGICAL_RESOURCE_CLUSTER_TEMPLATE}`)}}
                            />
                        </View>
                        <View style={{flexDirection: "row", marginTop: 12}}>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusStableImg/>
                                    <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                        Up
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                    {props.data.containerCluster.activeCount}
                                </Text>
                            </View>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusBlockImg/>
                                    <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                        Down
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                    {props.data.containerCluster.totalCount - props.data.containerCluster.activeCount}
                                </Text>
                            </View>
                        </View>
                        <View style={{alignItems: "center", marginTop: "auto"}}>
                            <View style={{...styles.TotalBox, width: "100%"}}>
                                <Text style={styles.TotalBoxText}>
                                    Total {props.data.containerCluster.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("NAMESPACE_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_NAMESPACE}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center", alignItems:"center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.namespace.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                <View>
                <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("CONTAINER_REGISTRY_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_LOGICAL_RESOURCE_REGISTRY}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center", alignItems:"center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.registry.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("CONTAINER_IMAGE_TEXT")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_IMAGE}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center", alignItems:"center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.containerImage.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.CONTAINER_LOGICAL_RESOURCE_NODE}
                    title={t("NODE_CPU_USAGE_TEXT")}
                    data={props.data.node.cpuUsedCount}
                    total={props.data.node.cpuTotalCount}
                    percent={percentValue(props.data.node.cpuUsedCount, props.data.node.cpuTotalCount)}
                    chartData={nodeCpuChartData}
                    img={<CpuBigImg/>}
                    color={"#ce4ffd"}
                    unit={"vCPU"}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.CONTAINER_LOGICAL_RESOURCE_NODE}
                    title={t("NODE_MEMORY_USAGE_TEXT")}
                    data={props.data.node.memoryUsedCapacityMb}
                    total={props.data.node.memoryTotalCapacityMb}
                    percent={percentValue(props.data.node.memoryUsedCapacityMb, props.data.node.memoryTotalCapacityMb)}
                    chartData={nodeMemoryChartData}
                    img={<MemBigImg/>}
                    color={"#7b4fff"}
                    unit={'MB'}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.CONTAINER_LOGICAL_RESOURCE_NODE}
                    title={t("NODE_STORAGE_USAGE_TEXT")}
                    data={props.data.node.storageUsedCapacityGb}
                    total={props.data.node.storageTotalCapacityGb}
                    percent={percentValue(props.data.node.storageUsedCapacityGb, props.data.node.storageTotalCapacityGb)}
                    chartData={nodeStorageChartData}
                    img={<GPUImg/>}
                    color={"#06c69d"}
                    unit={"GB"}
                />
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("Deployment")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_APPLICATION_DEPLOYMENT}`)}}
                            />
                        </View>
                        <View style={{flexDirection: "row", marginTop: 12}}>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusStableImg/>
                                    <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                        Up
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                    {props.data.deployment.activeCount}
                                </Text>
                            </View>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusBlockImg/>
                                    <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                        Down
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                    {props.data.deployment.totalCount - props.data.deployment.activeCount}
                                </Text>
                            </View>
                        </View>
                        <View style={{alignItems: "center", marginTop: "auto"}}>
                            <View style={{...styles.TotalBox, width: "100%"}}>
                                <Text style={styles.TotalBoxText}>
                                    Total {props.data.deployment.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("Pod")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_APPLICATION_POD}`)}}
                            />
                        </View>
                        <View style={{flexDirection: "row", marginTop: 12}}>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusStableImg/>
                                    <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                        Up
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                    {props.data.pod.activeCount}
                                </Text>
                            </View>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusBlockImg/>
                                    <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                        Down
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                    {props.data.pod.totalCount - props.data.pod.activeCount}
                                </Text>
                            </View>
                        </View>
                        <View style={{alignItems: "center", marginTop: "auto"}}>
                            <View style={{...styles.TotalBox, width: "100%"}}>
                                <Text style={styles.TotalBoxText}>
                                    Total {props.data.pod.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>
                <View>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("PV")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_POD_VOLUME}`)}}
                            />
                        </View>
                        <View style={{flexDirection: "row", marginTop: 12}}>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusStableImg/>
                                    <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                        Up
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                    {props.data.pvc.activeCount}
                                </Text>
                            </View>
                            <View style={{width: 60}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <StatusBlockImg/>
                                    <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                        Down
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                    {props.data.pvc.totalCount - props.data.pvc.activeCount}
                                </Text>
                            </View>
                        </View>
                        <View style={{alignItems: "center", marginTop: "auto"}}>
                            <View style={{...styles.TotalBox, width: "100%"}}>
                                <Text style={styles.TotalBoxText}>
                                    Total {props.data.pvc.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                    <DashboardBox style={{
                        width: 160, height: 155,
                        marginTop: 10,
                        marginRight: 10,
                    }}>
                        <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                            <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                                {t("Service")}
                            </Text>
                            <MoreImg 
                                style={{marginLeft: "auto", cursor: "pointer"}}
                                onClick={() => {navigate(`${PAGE_URL.CONTAINER_CLUSTER_MGMT_SERVICE}`)}}
                            />
                        </View>
                        <View style={{marginTop: 25}}>
                            <View style={{flexDirection: "row", justifyContent: "center", alignItems:"center"}}>
                                <View style={{flexDirection: "row", alignItems: "center"}}>
                                    <Text style={{...styles.Text, color: "#333", marginLeft: 3}}>
                                        {t("TOTAL_TEXT")}
                                    </Text>
                                </View>
                                <Text style={{...styles.MediumText, marginLeft: 20, color: "#3a8344"}}>
                                    {props.data.service.totalCount}
                                </Text>
                            </View>
                        </View>
                    </DashboardBox>
                </View>

            </View>

                {/* <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("HOST_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    up
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.host.active}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    down
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.host.total - props.data.host.active}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.host.total}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>

                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("STORAGE_TEXT")}
                        </Text>
                        <MoreImg 
                            style={{marginLeft: "auto", cursor: "pointer"}}
                            onClick={() => {navigate(`${PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_STORAGE}`)}}
                        />
                    </View>
                    <View style={{flexDirection: "row", marginTop: 12}}>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusStableImg/>
                                <Text style={{...styles.Text, color: "#3a8344", marginLeft: 3}}>
                                    up
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#3a8344"}}>
                                {props.data.storage.active}
                            </Text>
                        </View>
                        <View style={{width: 60}}>
                            <View style={{flexDirection: "row", alignItems: "center"}}>
                                <StatusBlockImg/>
                                <Text style={{...styles.Text, color: "#8c8c8c", marginLeft: 3}}>
                                    down
                                </Text>
                            </View>
                            <Text style={{...styles.MediumText, marginLeft: 5, color: "#8c8c8c"}}>
                                {props.data.storage.total - props.data.storage.active}
                            </Text>
                        </View>
                    </View>
                    <View style={{alignItems: "center", marginTop: "auto"}}>
                        <View style={{...styles.TotalBox, width: "100%"}}>
                            <Text style={styles.TotalBoxText}>
                                Total {props.data.storage.total}
                            </Text>
                        </View>
                    </View>
                </DashboardBox>

                <DashboardBox style={{
                    width: 160, height: 160,
                    marginRight: 10,
                }}>
                    <View style={{flexDirection: "row", alignItems: "center", width: "100%", height: 20}}>
                        <Text style={{...styles.BoxTitleText, marginRight: "auto"}}>
                            {t("OS_STATUS_TEXT")}
                        </Text>
                    </View>

                    <View style={{marginTop: 5}}>
                        <View style={{flexDirection: "row", marginBottom: 3}}>
                            <View style={{width: 50, marginRight: "auto"}}>
                                <Text style={styles.OSTitle}>
                                    Win
                                </Text>
                                <Text style={styles.MediumText}>
                                    {props.data.os.win}
                                </Text>
                            </View>
                            <View style={{width: 50}}>
                                <Text style={styles.OSTitle}>
                                    Linux
                                </Text>
                                <Text style={styles.MediumText}>
                                    {props.data.os.linux}
                                </Text>
                            </View>
                        </View>
                        <View style={{flexDirection: "row"}}>
                            <View style={{width: 50, marginRight: "auto"}}>
                                <Text style={styles.OSTitle}>
                                    Ubuntu
                                </Text>
                                <Text style={styles.MediumText}>
                                    {props.data.os.ubuntu}
                                </Text>
                            </View>
                            <View style={{width: 50}}>
                                <Text style={styles.OSTitle}>
                                    Others
                                </Text>
                                <Text style={{...styles.MediumText, color: "#ccc"}}>
                                    {props.data.os.others}
                                </Text>
                            </View>
                        </View>
                    </View>
                </DashboardBox>

                <HorizontalProgressBox
                    title={t("PROJECT_INFO_TEXT")}
                    url={PAGE_URL.APPLICATION_PROJECT}
                    navigate={navigate}
                    data={props.data.project.active}
                    total={props.data.project.total}
                />

                <SmallProgressBarBox
                    title={t("PORT_CURRENT_TEXT")}
                    url={PAGE_URL.VIRTUAL_PHYSICAL_RESOURCE_HOST}
                    navigate={navigate}
                    data={props.data.port.active}
                    total={props.data.port.total}
                />

                <SmallProgressBarBox
                    title={t("ROUTER_CURRENT_TEXT")}
                    url={PAGE_URL.CONTAINER_CLUSTER_MGMT_IMAGE}
                    navigate={navigate}
                    data={props.data.router.active}
                    total={props.data.router.total}
                /> */}

               
            {/* 2 Line */}
            {/* <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <DoughnutChartBlueBox
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM}
                    title={t("VM_TEXT")}
                    data={props.data.vm.avail}
                    total={props.data.vm.total}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM}
                    title={t("CPU_USAGE_TEXT")}
                    data={props.data.cpu.avail}
                    total={props.data.cpu.total}
                    percent={props.data.cpu.percent}
                    chartData={props.data.cpu.chartData}
                    img={<CpuBigImg/>}
                    color={"#ce4ffd"}
                    unit={"vCPU"}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM}
                    title={t("MEM_USAGE_TEXT")}
                    data={props.data.mem.avail}
                    total={props.data.mem.total}
                    percent={props.data.mem.percent}
                    chartData={props.data.mem.chartData}
                    img={<MemBigImg/>}
                    color={"#7b4fff"}
                    unit={"GB"}
                />
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VOLUMNE}
                    title={t("VOLUME_USAGE_TEXT")}
                    data={props.data.volumeSize.avail}
                    total={props.data.volumeSize.total > 1000 ? Number((props.data.volumeSize.total/1000).toFixed(1)) : props.data.volumeSize.total}
                    percent={props.data.volumeSize.percent}
                    chartData={props.data.volumeSize.chartData}
                    img={<VolumeImg/>}
                    color={"#06c69d"}
                    unit={props.data.volumeSize.total > 1000 ? "TB" : "GB"}
                />
            </View> */}

            {/* 3 Line */}
            {/* <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_NETWORK}
                    title={t("NETWORK_INFO_TEXT")}
                    data={props.data.net.avail}
                    total={props.data.net.total}
                    percent={props.data.net.percent}
                    chartData={props.data.net.chartData}
                    img={<NetworkImg/>}
                    color={"#9ca3ad"}
                    unit={"vNet"}
                />
                <BoldDoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_SECURITY_RULE}
                    title={t("FLOATING_IP_TEXT")}
                    data={props.data.floatingIp.avail}
                    total={props.data.floatingIp.total}
                    chartData={props.data.floatingIp.chartData}
                />

                <BarChartBox
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_SECURITY}
                    title={t("SECURITY_GROUP_TEXT")}
                    data={props.data.security.avail}
                    total={props.data.security.total}
                    color={"rgba(74, 119, 255, 0.2)"}
                    secondcolor={"#4a77ff"}
                />
                <BarChartBox
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_LOGICAL_RESOURCE_SECURITY_RULE}
                    title={t("SECURITY_GROUP_RULE_TEXT")}
                    data={props.data.securityRule.avail}
                    total={props.data.securityRule.total}
                    color={"rgba(74, 119, 255, 0.5)"}
                    secondcolor={"#ce4ffd"}
                />

                
            </View> */}

            {/* 4 Line */}
            {/* <View style={{flexDirection: "row", alignItems: "center", marginBottom: 10}}>
                <PieChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_RESOURCE_GROUP_HOST_CLUSTER}
                    title={t("HOST_CLUSTER_TEXT")}
                    data={props.data.hostCluster.avail}
                    total={props.data.hostCluster.total}
                    chartData={props.data.hostCluster.chartData}
                    color={"#9ca3ad"}
                />

                <DoughnutChart
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VM}
                    title={t("GPU_TEXT")}
                    data={props.data.gpu.avail}
                    total={props.data.gpu.total}
                    percent={props.data.gpu.percent}
                    chartData={props.data.gpu.chartData}
                    img={<GPUImg/>}
                    color={"#4a77ff"}
                    unit={"개"}
                />

                <VerticalProgress
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VOLUMNE}
                    title={t("SNAPSHOT_TYPE_TEXT")}
                    data={props.data.snapshot.avail}
                    total={props.data.snapshot.total}
                    percent={props.data.snapshot.percent}
                    color={"#0853fb"}
                    unit={"개"}
                />

                <MatrixChartBox
                    navigate={navigate}
                    url={PAGE_URL.VIRTUAL_INSTANCE_MGMT_VOLUMNE}
                    title={t("VOLUME_TYPE_TEXT")}
                    data={props.data.volume.avail}
                    total={props.data.volume.total}
                />
            </View> */}
        </Fragment>
    )
}


const styles = StyleSheet.create({
    BoxTitleText: {
        fontFamily: FONT.MEDIUM,
        fontSize: 14,
        lineHeight: 13,
        color: "#262626"
    },
    Text: {
        fontFamily: FONT.REGULAR,
        fontSize: 12,
        color: "#333"
    },
    MediumText: {
        fontFamily: FONT.MEDIUM,
        fontSize: 24,
        color: "#333"
    },
    SmallText: {
        fontFamily: FONT.REGULAR,
        fontSize: 10,
        color: "#808080"
    },
    BigText: {
        fontFamily: FONT.BOLD,
        fontSize: 32,
        color: "#333"
    },
    TotalBox: {
        flexDirection: "row", alignItems: "center", justifyContent: "center",
        minWidth: 80, height: 24,
        paddingLeft: 10, paddingRight: 10,
        borderRadius: 18,
        borderWidth: 1, borderColor: "#e4e1eb"
    },
    TotalBoxText: {
        fontFamily: FONT.REGULAR,
        fontSize: 10,
        color: "#808080"
    },
    Line: {
        borderTopWidth: 1, borderColor: "#e3e1ea",
    },
    OSTitle: {
        fontFamily: FONT.REGULAR,
        fontSize: 12,
        color: "#8c8c8c"
    },
})